import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Carousel from "./Carousel";
import { FaArrowCircleLeft } from "react-icons/fa";
import { researchProjects } from "../data/researchprojects";
import { schoolProjects } from "../data/schoolprojects";
import { otherProjects } from "../data/otherprojects";
import { phdResearchProjects } from "../data/phdResearchProjects";
import { collaborators } from "../data/collaborators";
import { mentoring } from "../data/mentoring";
import ProjectPublicationList from "./ProjectPublicationList";
import PillarChips from "./PillarChips";
import AssociationsDisplay from "./AssociationsDisplay";
import { formatHtml } from "../utils/formatHtml";
import {
  resolveProjectCollaborators,
  resolveProjectMentees,
} from "../utils/relationSync";
import { associationTypes } from "../data/associationTypes";
import { isProjectShown } from "../utils/projects";
import {
  collectProjectPublicationRefs,
  partitionProjectPublications,
} from "../utils/projectContent";

const renderDescriptionBlocks = (description) =>
  description.split("\n\n").map((block, i) => (
    <div key={i}>
      {block.split("\n").map((line, j) =>
        line.includes("<") ? (
          <div
            key={j}
            className="mt-1 text-sm leading-relaxed text-gray-700 dark:text-gray-300 [&_a]:text-green dark:[&_a]:text-green-dark [&_a]:underline [&_a]:underline-offset-2"
            dangerouslySetInnerHTML={{ __html: formatHtml(line) }}
          />
        ) : (
          <p
            className="mt-1 text-sm leading-relaxed text-gray-700 dark:text-gray-300"
            key={j}
          >
            {line}
          </p>
        ),
      )}
      {i < description.split("\n\n").length - 1 && <br />}
    </div>
  ));

const ProjectViewMore = () => {
  const params = useParams();
  const location = useLocation();
  const [projectData, setProjectData] = useState(null);

  useEffect(() => {
    let project = phdResearchProjects.find(
      (entry) => entry.id === params.id && isProjectShown(entry),
    );
    let collection = "phd";
    if (!project) {
      project = researchProjects.find((entry) => entry.id === params.id);
      collection = "pre-phd";
    }
    if (!project) {
      project = schoolProjects.find((entry) => entry.id === params.id);
      collection = "pre-phd";
    }
    if (!project) {
      project = otherProjects.find((entry) => entry.id === params.id);
      collection = "pre-phd";
    }
    setProjectData(project ? { ...project, _collection: collection } : null);
  }, [params.id]);

  const backTo =
    location.state?.backTo ??
    (projectData?._collection === "pre-phd"
      ? "/pre-phd?tab=projects"
      : "/home?view=projects");
  const backLabel =
    location.state?.backLabel ??
    (projectData?._collection === "pre-phd" ? "Background" : "Research");

  const projectCollaborators = useMemo(
    () => resolveProjectCollaborators(projectData, collaborators),
    [projectData],
  );

  const projectMentees = useMemo(
    () => resolveProjectMentees(projectData, mentoring),
    [projectData],
  );

  const { relatedPublications, workshops } = useMemo(
    () =>
      partitionProjectPublications(
        collectProjectPublicationRefs(projectData),
      ),
    [projectData],
  );

  if (!projectData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Project not found
      </div>
    );
  }

  const isPhdProject = Boolean(projectData.contributions);
  const {
    title,
    img,
    role,
    duration,
    summary,
    contributions = [],
    pillarIds = [],
    carouselData = [],
    description,
    github,
    report,
    youtube,
  } = projectData;

  const buttonClass =
    "rounded bg-darkBg text-darkText dark:bg-lightBg dark:text-lightText hover:bg-green-solid py-2 px-4 hover:text-white dark:hover:bg-green-solid dark:hover:text-white text-sm";

  return (
    <div className="flex items-center justify-center min-h-screen bg-lightBg dark:bg-darkBg px-6 py-6 sm:px-8 lg:px-12">
      <div className="w-full max-w-4xl mx-auto bg-white dark:bg-darkBg border-2 border-opacity-25 dark:border-2 dark:border-opacity-25 border-lightText dark:border-darkText rounded-md p-6 md:p-8 shadow-lg">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
          <Link
            to={backTo}
            className="text-green dark:text-green-dark text-2xl lg:text-3xl hover:opacity-80 transition"
            title="Back"
          >
            <FaArrowCircleLeft />
          </Link>

          <div className="flex items-center ml-2">
            <Link to={backTo} className="text-green dark:text-green-dark underline underline-offset-2">
              {backLabel}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {title}
            </span>
          </div>
        </div>

        {img && (
          <div className="mb-5 flex max-h-52 w-full items-center justify-center rounded-md border border-gray-200/70 bg-white p-4 dark:border-gray-800/70 dark:bg-darkBg">
            <img
              src={img}
              alt={title}
              className="max-h-44 max-w-full object-contain"
            />
          </div>
        )}

        <h1 className="text-2xl xl:text-3xl font-bold text-lightText dark:text-darkText mb-2">
          {title}
        </h1>

        {isPhdProject ? (
          <>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{role}</p>
            {duration && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {duration}
              </p>
            )}

            {summary && (
              <div className="mt-6">
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Overview
                </h2>
                <p className="text-sm italic leading-relaxed text-gray-700 dark:text-gray-300">
                  {summary}
                </p>
                <PillarChips pillarIds={pillarIds} />
                <AssociationsDisplay
                  className="mt-4"
                  associations={projectData.associations}
                  associationTypes={associationTypes}
                  excludeTypeIds={["projects", "pillars", "collaborators", "mentees"]}
                />
              </div>
            )}

            {contributions.length > 0 && (
              <div className="mt-6">
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Contributions
                </h2>
                <ul className="list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                  {contributions.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </div>
            )}

            {(projectCollaborators.length > 0 || projectMentees.length > 0) && (
              <div className="mt-6">
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5">
                  Team
                </h2>
                {projectCollaborators.length > 0 && (
                  <div className="mb-3">
                    <p className="text-base font-semibold text-gray-700 dark:text-gray-300">
                      Collaborators
                    </p>
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                      {projectCollaborators.map((person) => (
                        <li key={person.id}>
                          {person.name} ({person.designation})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {projectMentees.length > 0 && (
                  <div>
                    <p className="text-base font-semibold text-gray-700 dark:text-gray-300">
                      Mentees
                    </p>
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                      {projectMentees.map((person) => (
                        <li key={person.id}>
                          {person.name} ({person.designation}; {person.duration})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <ProjectPublicationList
              title="Publications / Papers"
              items={relatedPublications}
              headingClassName="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5"
              className="mt-6"
            />
            <ProjectPublicationList
              title="Workshops"
              items={workshops}
              showWorkshopTitle
              headingClassName="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mb-1.5"
              className="mt-6"
            />
          </>
        ) : (
          <>
            <div className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 mb-4">
              <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mt-6 mb-1.5">
                About the Project
              </h2>
              {description ? renderDescriptionBlocks(description) : null}
            </div>

            {carouselData.length > 0 && (
              <>
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mt-6 mb-1.5">
                  Gallery
                </h2>
                <div className="self-center my-4">
                  <Carousel carouselData={carouselData} />
                </div>
              </>
            )}

            {youtube && (
              <>
                <h2 className="text-xl xl:text-2xl font-semibold text-lightText dark:text-darkText mt-6 mb-1.5">
                  Demo Video
                </h2>
                <div className="aspect-w-16 aspect-h-9 mb-6 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                  <iframe
                    src={youtube}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="YouTube Video"
                    className="w-full h-full"
                  ></iframe>
                </div>
              </>
            )}

            <div className="flex flex-wrap mt-8">
              {github && (
                <a
                  href={github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mr-4 mb-4"
                >
                  <button className={buttonClass}>
                    View GitHub Repository!
                  </button>
                </a>
              )}
              {report && (
                <a
                  href={report}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mr-4 mb-4"
                >
                  <button className={buttonClass}>Read More!</button>
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectViewMore;
